import { useState } from 'react';
import {
  AccessDraft,
  AssignableApp,
  CatalogApp,
  MainRole,
  effectiveSummary,
  isGranted,
  roleCanHold,
  seedOverrideFromInherited,
  tierForMainRole,
  toggleAction,
} from './accessModel';

/**
 * Application Access — Central's canonical access editor.
 *
 * One control for invite creation and member editing, in every workspace
 * type. Two independent axes, shown together:
 *
 *   Application access   which ASSIGNABLE apps this person gets. The list is
 *                        Central's assignable-apps answer, never a local one.
 *   Per-app permissions  for an assigned app, either the main role's default
 *                        ("Role default", inherited) or an explicit list
 *                        ("Customized"), shown as the app's own vocabulary —
 *                        never raw scope strings.
 *
 * Department, Team and Subrole are separate axes and do not appear here: the
 * editor renders and edits the same way whatever they are set to.
 */
export interface ApplicationAccessEditorProps {
  apps: readonly AssignableApp[];
  catalog: readonly CatalogApp[];
  mainRole: MainRole;
  value: AccessDraft;
  onChange: (next: AccessDraft) => void;
  readOnly?: boolean;
  /** Message when Central could not supply the app list. */
  unavailableReason?: string | null;
  idPrefix?: string;
}

export function ApplicationAccessEditor({
  apps,
  catalog,
  mainRole,
  value,
  onChange,
  readOnly = false,
  unavailableReason = null,
  idPrefix = 'access',
}: ApplicationAccessEditorProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const byId = new Map(catalog.map((c) => [c.appId, c]));
  const selectedCount = apps.filter((a) => a.appId in value.appRoles).length;

  function setAppOn(app: AssignableApp, on: boolean) {
    const appRoles = { ...value.appRoles };
    const overrides = { ...value.overrides };
    if (on) appRoles[app.appId] = tierForMainRole(mainRole);
    else delete appRoles[app.appId];
    onChange({ appRoles, overrides });
  }

  function setOverride(appId: string, next: string[] | null) {
    onChange({ appRoles: value.appRoles, overrides: { ...value.overrides, [appId]: next } });
  }

  return (
    <section data-testid="application-access" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700">Application access</h4>
          <p className="text-xs text-gray-500">
            Choose the apps this person gets. Their level in each follows the main role.
          </p>
        </div>
        {apps.length > 0 && (
          <span data-testid="application-access-count" className="shrink-0 text-xs text-gray-500">
            {selectedCount} of {apps.length}
          </span>
        )}
      </div>

      {unavailableReason ? (
        <p data-testid="application-access-unavailable" className="text-sm text-amber-700">
          {unavailableReason}
        </p>
      ) : apps.length === 0 ? (
        <p data-testid="application-access-empty" className="text-sm text-gray-500">
          This workspace has no assignable applications.
        </p>
      ) : (
        <ul className="space-y-2">
          {apps.map((app) => {
            const on = app.appId in value.appRoles;
            const locked = readOnly || !app.assignable;
            const cat = byId.get(app.appId);
            const override = value.overrides[app.appId] ?? null;
            const open = on && (expanded[app.appId] ?? false);
            const summary = cat ? effectiveSummary(cat, mainRole, override) : [];
            const id = `${idPrefix}-app-${app.appId}`;
            return (
              <li
                key={app.appId}
                data-testid={`access-app-${app.appId}`}
                className={`rounded-lg border ${on ? 'border-emerald-200' : 'border-gray-200'}`}
              >
                <div className="flex items-center gap-3 px-3 py-2">
                  <input
                    id={id}
                    type="checkbox"
                    checked={on}
                    disabled={locked}
                    aria-label={`${app.displayName} access`}
                    data-testid={`access-app-toggle-${app.appId}`}
                    onChange={(e) => setAppOn(app, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                  />
                  <label htmlFor={id} className="flex-1 text-sm font-medium text-gray-900">
                    {app.displayName}
                  </label>
                  {on && (
                    <span data-testid={`access-app-mode-${app.appId}`} className="text-xs text-gray-500">
                      {override === null ? 'Role default' : 'Customized'}
                    </span>
                  )}
                  {!app.assignable && (
                    <span className="text-xs text-gray-400">Not yours to assign</span>
                  )}
                  {on && cat && (
                    <button
                      type="button"
                      data-testid={`access-app-expand-${app.appId}`}
                      onClick={() => setExpanded((p) => ({ ...p, [app.appId]: !open }))}
                      className="text-xs font-medium text-emerald-700"
                    >
                      {open ? 'Hide permissions' : 'Permissions'}
                    </button>
                  )}
                </div>

                {on && !open && summary.length > 0 && (
                  <p data-testid={`access-app-summary-${app.appId}`} className="px-3 pb-2 text-xs text-gray-500">
                    {summary.length} area{summary.length === 1 ? '' : 's'}:{' '}
                    {summary.slice(0, 4).map((s) => s.label).join(', ')}
                    {summary.length > 4 ? '…' : ''}
                  </p>
                )}

                {open && cat && (
                  <PermissionTree
                    app={cat}
                    mainRole={mainRole}
                    override={override}
                    readOnly={readOnly}
                    onCustomize={() => setOverride(app.appId, seedOverrideFromInherited(cat, mainRole))}
                    onReset={() => setOverride(app.appId, null)}
                    onChange={(next) => setOverride(app.appId, next)}
                    idPrefix={`${idPrefix}-${app.appId}`}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function PermissionTree({
  app,
  mainRole,
  override,
  readOnly,
  onCustomize,
  onReset,
  onChange,
  idPrefix,
}: {
  app: CatalogApp;
  mainRole: MainRole;
  override: string[] | null;
  readOnly: boolean;
  onCustomize: () => void;
  onReset: () => void;
  onChange: (next: string[]) => void;
  idPrefix: string;
}) {
  const inherited = override === null;
  const groups: string[] = [];
  for (const d of app.domains) if (!groups.includes(d.group)) groups.push(d.group);

  return (
    <div data-testid={`access-tree-${app.appId}`} className="space-y-3 border-t border-gray-100 px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-600">
          {inherited
            ? `Using the ${mainRole} role's default permissions.`
            : 'Customized for this person — the role default no longer applies to this app.'}
        </p>
        {!readOnly &&
          (inherited ? (
            <button
              type="button"
              data-testid={`access-customize-${app.appId}`}
              onClick={onCustomize}
              className="shrink-0 text-xs font-medium text-emerald-700"
            >
              Customize
            </button>
          ) : (
            <button
              type="button"
              data-testid={`access-reset-${app.appId}`}
              onClick={onReset}
              className="shrink-0 text-xs font-medium text-gray-600"
            >
              Reset to role default
            </button>
          ))}
      </div>

      {mainRole === 'viewer' && (
        <p className="text-xs text-gray-500">Viewers can only view and respond for themselves.</p>
      )}

      {groups.map((group) => (
        <div key={group}>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{group}</p>
          <ul className="space-y-1.5">
            {app.domains
              .filter((d) => d.group === group)
              .map((d) => (
                <li key={d.domain} data-testid={`access-domain-${app.appId}-${d.domain}`}>
                  <p className="text-sm text-gray-800">{d.label}</p>
                  <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-1">
                    {d.actions.map((a) => {
                      const id = `${idPrefix}-${d.domain}-${a.action}`;
                      const granted = isGranted(a, mainRole, override, app.appId);
                      const disabled =
                        readOnly || inherited || a.scope === null || !roleCanHold(a, mainRole);
                      return (
                        <label key={a.action} htmlFor={id} className="flex items-center gap-1.5 text-xs text-gray-700">
                          <input
                            id={id}
                            type="checkbox"
                            checked={granted}
                            disabled={disabled}
                            data-testid={`access-action-${app.appId}-${d.domain}-${a.action}`}
                            onChange={(e) => onChange(toggleAction(override ?? [], d, a, e.target.checked))}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600"
                          />
                          {a.label}
                        </label>
                      );
                    })}
                  </div>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
